/**
 * WeeklyCalendarClient.tsx
 *
 * Root client component for the TarotCal application.
 *
 * Responsibilities:
 * - Renders the full-screen weekly tarot calendar (7 day cells + mini-month panel).
 * - Manages week navigation (forward/backward with slide animations).
 * - Handles card overrides: a specific card can be force-assigned to any date via
 *   voice recognition or the config modal; overrides are persisted in a cookie.
 * - Orchestrates the splash screen and optional shuffle-on-start flow.
 * - Owns the tilt-gesture detector that reads the last-viewed card name from
 *   `sessionStorage` and briefly displays it in the header subtitle.
 * - Exposes a long-press gesture on the title that also reveals the stored card name.
 * - Manages all modal/overlay state: card popup, config modal, reading calculator,
 *   go-to-date popup.
 *
 * ## Tilt gesture flow
 * 1. User opens a card popup → popup stores card name in `sessionStorage`.
 * 2. User closes the popup → `handlePopupClosedForTilt` calls `enableTiltDetection`.
 * 3. `enableTiltDetection` requests iOS motion permission if needed, then calls
 *    `armTiltDetection`.
 * 4. `armTiltDetection` registers a single `deviceorientation` listener (guarded by
 *    `tiltListenerAttached` ref to prevent duplicates).
 * 5. The listener runs a three-step state machine:
 *    `waitingReady` → `waitingForward` → `waitingReturn` → fires `onTilted`.
 * 6. `onTilted` reads the stored card name, updates `headerSubtitle` for 5 s, and
 *    dispatches a `tarot-card-name-updated` custom event.
 *
 * ## Cookie / storage keys
 * | Key                              | Storage    | Purpose                                  |
 * |----------------------------------|------------|------------------------------------------|
 * | `tarot-override-card`            | Cookie     | Persists the force-card selection        |
 * | `tarot-shuffle-on-start`         | localStorage | Whether to shuffle the deck on load    |
 * | `tarot-last-displayed-card-name` | sessionStorage | Last card name shown in popup        |
 */

'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCardForDate, THE_MAGICIAN, TAROT_CARDS, setSessionCalendar, type TarotCard } from '@/data/tarotData';
import DayCell from './DayCell';
import MiniMonthCalendar from './MiniMonthCalendar';
import TarotCardPopup from './TarotCardPopup';
import NavigationControls from './NavigationControls';
import AmbientParticles from './AmbientParticles';
import ConfigModal from './ConfigModal';
import CardPickerCalculator from './CardPickerCalculator';
import GoToDatePopup from './GoToDatePopup';
import SplashScreen, { type SplashMode } from './SplashScreen';

// ── Peek card name formatter ──────────────────────────────────────────────────
// Used ONLY in the peek reveal to format the subtitle display.
// Minor arcana: replaces word numbers with digits (e.g. "Four of Swords" → "4 of Swords").
// Page / Knight / Queen / King are left unchanged.
// Major arcana: prepends the card's numerical value (e.g. "The Moon" → "18 - The Moon").
const WORD_TO_DIGIT: Record<string, string> = {
  Ace: 'A', Two: '2', Three: '3', Four: '4', Five: '5',
  Six: '6', Seven: '7', Eight: '8', Nine: '9', Ten: '10',
};

// Major arcana ordered 0–21 matching TAROT_CARDS sequence
const MAJOR_ARCANA_NUMBERS: Record<string, number> = {
  'The Fool': 0, 'The Magician': 1, 'The High Priestess': 2, 'The Empress': 3,
  'The Emperor': 4, 'The Hierophant': 5, 'The Lovers': 6, 'The Chariot': 7,
  'Strength': 8, 'The Hermit': 9, 'Wheel of Fortune': 10, 'Justice': 11,
  'The Hanged Man': 12, 'Death': 13, 'Temperance': 14, 'The Devil': 15,
  'The Tower': 16, 'The Star': 17, 'The Moon': 18, 'The Sun': 19,
  'Judgement': 20, 'The World': 21,
};

function formatPeekCardName(name: string): string {
  // Check major arcana first
  if (name in MAJOR_ARCANA_NUMBERS) {
    return `${MAJOR_ARCANA_NUMBERS[name]} - ${name}`;
  }
  // Minor arcana: replace leading word number with digit
  const firstWord = name.split(' ')[0];
  if (firstWord in WORD_TO_DIGIT) {
    return WORD_TO_DIGIT[firstWord] + name.slice(firstWord.length);
  }
  // Page / Knight / Queen / King — unchanged
  return name;
}
// ─────────────────────────────────────────────────────────────────────────────

/** Cookie name used to persist the force-override card selection across sessions. */
const OVERRIDE_CARD_COOKIE = 'tarot-override-card';

/** localStorage key that stores the "shuffle on start" boolean preference. */
const SHUFFLE_STORAGE_KEY = 'tarot-shuffle-on-start';

/** sessionStorage key holding the name of the last card displayed in the popup. */
const STORED_CARD_NAME_KEY = 'tarot-last-displayed-card-name';

/**
 * Set to `true` to show the hidden -DBG- gesture tester menu item.
 * Set to `false` to hide it (page is still distributed but not accessible via menu).
 */
const SHOWTEST = true;

/**
 * Persist the selected override card ID to a 1-year cookie.
 *
 * @param cardId - The `TarotCard.id` to store.
 */
function saveOverrideCardToCookie(cardId: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${OVERRIDE_CARD_COOKIE}=${encodeURIComponent(cardId)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch { /* ignore */ }
}

/**
 * Read the override card from the cookie and return the matching `TarotCard`.
 * Falls back to `THE_MAGICIAN` if the cookie is absent or the ID is unrecognised.
 *
 * @returns The persisted override card, or `THE_MAGICIAN` as a safe default.
 */
function loadOverrideCardFromCookie(): TarotCard {
  if (typeof window === 'undefined' || typeof document === 'undefined') return THE_MAGICIAN;
  try {
    const match = document.cookie.split('; ').find(row => row.startsWith(`${OVERRIDE_CARD_COOKIE}=`));
    if (match) {
      const cardId = decodeURIComponent(match.split('=')[1]);
      const found = TAROT_CARDS.find(c => c.id === cardId);
      if (found) return found;
    }
  } catch { /* ignore */ }
  return THE_MAGICIAN;
}

/**
 * Data shape passed to each `DayCell` component.
 *
 * @property date           - The calendar date this cell represents.
 * @property card           - The card currently assigned to this date (may be overridden).
 * @property isToday        - Whether this date is today.
 * @property isCurrentMonth - Whether this date falls in the current calendar month.
 * @property isOverridden   - Whether the card has been manually overridden by the user.
 * @property isSelectedDate - Whether this date was explicitly selected via "Go to date".
 * @property originalCard   - The unoverridden card from the static/shuffled calendar.
 */
export interface DayCellData {
  date: Date;
  card: TarotCard | undefined;
  isToday: boolean;
  isCurrentMonth: boolean;
  isOverridden: boolean;
  isSelectedDate: boolean;
  originalCard: TarotCard | undefined;
}

/**
 * Return the Monday that starts the ISO week containing `date`.
 *
 * @param date - Any date within the target week.
 * @returns A new `Date` set to midnight on the Monday of that week.
 */
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Return an array of 7 consecutive dates starting from `weekStart` (Monday).
 *
 * @param weekStart - The Monday that begins the week.
 * @returns Array of 7 `Date` objects: Mon, Tue, Wed, Thu, Fri, Sat, Sun.
 */
function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

export default function WeeklyCalendarClient() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(today));
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [animating, setAnimating] = useState(false);
  const [pendingWeekStart, setPendingWeekStart] = useState<Date | null>(null);
  const [overrides, setOverrides] = useState<Record<string, TarotCard>>({});
  const [popupCard, setPopupCard] = useState<{ card: TarotCard; date: Date } | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [voiceResults, setVoiceResults] = useState<Record<string, 'blue' | 'red'>>({});
  const [overrideCard, setOverrideCard] = useState<TarotCard>(THE_MAGICIAN);
  const [storedCardName, setStoredCardName] = useState<string | null>(null);
  const [headerSubtitle, setHeaderSubtitle] = useState('The Oracle of Days');

  // Secret peek reveal state — temporarily replaces headerSubtitle
  const [revealedCardName, setRevealedCardName] = useState<string | null>(null);

  // Splash screen state
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashMode, setSplashMode] = useState<SplashMode>(0);
  const [appReady, setAppReady] = useState(false);

  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Reading mode calculator
  const [readingCalculatorOpen, setReadingCalculatorOpen] = useState(false);

  // Shuffle version counter — incrementing forces dayCells to re-read the session calendar
  const [shuffleVersion, setShuffleVersion] = useState(0);

  // Go to date popup
  const [goToDateOpen, setGoToDateOpen] = useState(false);
  const [selectedGoToDate, setSelectedGoToDate] = useState<Date | null>(null);

  // Month selector open state (to hide nav buttons)
  const [monthSelectorOpen, setMonthSelectorOpen] = useState(false);

  // Read speech delay from localStorage (set by ConfigModal)
  const [speechDelayMs, setSpeechDelayMs] = useState<number>(2 * 60 * 1000);

  const gridRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const overrideCardRef = useRef<TarotCard>(THE_MAGICIAN);
  const longPressTimer = useRef<number | null>(null);

  // ── Secret peek refs ──
  // Gesture state machine: 'idle' | 'waitingRaise'
  const peekState = useRef<'idle' | 'waitingRaise'>('idle');
  // Timestamp when the device first crossed the lower threshold
  const lowerEntryTime = useRef<number | null>(null);
  // Timestamp when the device first crossed the raise threshold
  const raiseEntryTime = useRef<number | null>(null);
  // Whether the orientation listener is currently attached
  const peekListenerAttached = useRef(false);
  // Whether iOS permission has already been requested this lifecycle
  const motionPermissionRequested = useRef(false);
  // Ref for the reveal timeout so we can clear/reset it on repeated peeks
  const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to the orientation handler function so we can remove it on unmount
  const peekOrientationHandler = useRef<((e: DeviceOrientationEvent) => void) | null>(null);
  // Baseline beta captured on first orientation event for calibration
  const baselineBeta = useRef<number | null>(null);
  // Wake lock sentinel ref
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Sustain durations (degrees)
  const LOWER_SUSTAIN_MS = 750;  // must hold lower for 750 ms
  const RAISE_SUSTAIN_MS = 500;  // must hold raise for 500 ms

  // ── Secret peek: arm the orientation listener after popup closes ──
  // Called by TarotCardPopup after iOS permission has already been requested there.
  const handlePopupClosedForTilt = useCallback(() => {
    if (peekListenerAttached.current) return; // already listening

    type DOEWithPermission = typeof DeviceOrientationEvent & {
      requestPermission?: () => Promise<string>;
    };
    const DOE = DeviceOrientationEvent as DOEWithPermission;

    const attachListener = () => {
      if (peekListenerAttached.current || typeof window === 'undefined') return;

      const handleOrientation = (event: DeviceOrientationEvent) => {
        const beta = event.beta;
        if (beta === null) return;

        // Calibrate on first reading
        if (baselineBeta.current === null) {
          baselineBeta.current = beta;
          return;
        }

        const LOWER_DELTA = -20;  // 20° below baseline = "lowered"
        const RAISE_DELTA = -8;   // within 8° of baseline = "raised back"

        const lowerThreshold = baselineBeta.current + LOWER_DELTA;
        const raiseThreshold = baselineBeta.current + RAISE_DELTA;

        const peekEnabled = sessionStorage.getItem('tarot-peek-enabled') === 'true';
        const hasCard = !!sessionStorage.getItem('tarot-last-displayed-card-name');

        if (peekState.current === 'idle') {
          if (!peekEnabled || !hasCard) return;

          if (beta <= lowerThreshold) {
            if (lowerEntryTime.current === null) {
              lowerEntryTime.current = Date.now();
            } else if (Date.now() - lowerEntryTime.current >= LOWER_SUSTAIN_MS) {
              // Lowering phase complete
              peekState.current = 'waitingRaise';
              lowerEntryTime.current = null;
              raiseEntryTime.current = null;
              console.log('Peek lowered');
            }
          } else {
            // Threshold not sustained — reset
            lowerEntryTime.current = null;
          }
        } else if (peekState.current === 'waitingRaise') {
          if (beta >= raiseThreshold) {
            if (raiseEntryTime.current === null) {
              raiseEntryTime.current = Date.now();
            } else if (Date.now() - raiseEntryTime.current >= RAISE_SUSTAIN_MS) {
              // Raising phase complete — trigger reveal
              raiseEntryTime.current = null;
              peekState.current = 'idle';
              baselineBeta.current = null; // re-calibrate next time
              console.log('Peek revealed');

              const cardName = sessionStorage.getItem('tarot-last-displayed-card-name');
              if (!cardName) return;

              // Clear any existing reveal timer and reset
              if (revealTimeoutRef.current !== null) {
                clearTimeout(revealTimeoutRef.current);
                revealTimeoutRef.current = null;
              }
                            setRevealedCardName(formatPeekCardName(cardName));
              revealTimeoutRef.current = setTimeout(() => {
                setRevealedCardName(null);
                revealTimeoutRef.current = null;
              }, 5000);
            }
          } else {
            // Threshold not sustained — reset raise timer
            raiseEntryTime.current = null;
          }
        }
      };

      // Chrome PWA prefers deviceorientationabsolute; listen to both and use
      // whichever fires first, then remove the other to avoid duplicate events.
      let absoluteFired = false;
      let standardFired = false;

      const handleAbsolute = (event: DeviceOrientationEvent) => {
        if (standardFired) return; // standard already won
        if (!absoluteFired) {
          absoluteFired = true;
          // absolute won — remove the standard listener
          window.removeEventListener('deviceorientation', handleStandard);
        }
        handleOrientation(event);
      };

      const handleStandard = (event: DeviceOrientationEvent) => {
        if (absoluteFired) return; // absolute already won
        if (!standardFired) {
          standardFired = true;
          // standard won — remove the absolute listener
          window.removeEventListener('deviceorientationabsolute', handleAbsolute as EventListener);
        }
        handleOrientation(event);
      };

      window.addEventListener('deviceorientationabsolute', handleAbsolute);
      window.addEventListener('deviceorientation', handleStandard);

      // Store a cleanup function that removes both listeners
      peekOrientationHandler.current = handleOrientation; // kept for reference
      // Override cleanup to remove both named handlers
      (peekOrientationHandler as React.RefObject<(() => void) | null>).current = () => { // was MutableRefObject, now deprecated
        window.removeEventListener('deviceorientationabsolute', handleAbsolute);
        window.removeEventListener('deviceorientation', handleStandard);
      };
      peekListenerAttached.current = true;

      // Acquire Wake Lock to prevent Chrome from suspending sensors when screen dims
      if ('wakeLock' in navigator && wakeLockRef.current === null) {
        navigator.wakeLock.request('screen').then((sentinel) => {
          wakeLockRef.current = sentinel;
          sentinel.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }).catch(() => { /* wake lock not available */ });
      }

      // One-time touchstart handler: Chrome PWA suspends the gyro until user
      // interaction. Re-arm the listener on the first touch after a resume.
      const reArmOnTouch = () => {
        window.removeEventListener('touchstart', reArmOnTouch);
        if (peekListenerAttached.current) {
          // Detach and re-attach to reset the sensor subscription
          if (typeof (peekOrientationHandler.current as unknown) === 'function') {
            (peekOrientationHandler.current as unknown as () => void)();
          }
          peekListenerAttached.current = false;
          baselineBeta.current = null;
          attachListener();
        }
      };
      window.addEventListener('touchstart', reArmOnTouch, { passive: true, once: true });
    };

    // On iOS, permission was already requested by TarotCardPopup's close handler.
    // We just need to check if it was granted (or if we're on a non-iOS device).
    if (typeof DOE?.requestPermission === 'function') {
      DOE.requestPermission().then((result) => {
        if (result === 'granted') {
          attachListener();
        } else {
          console.log('Motion permission denied');
          peekListenerAttached.current = false;
        }
      }).catch(() => {
        console.log('Motion permission denied');
        peekListenerAttached.current = false;
      });
    } else {
      // Android / desktop — no permission needed
      attachListener();
    }
  }, []); // constants LOWER_THRESHOLD etc. are stable literals

  useEffect(() => {
    const rearmPeekListener = () => {
      if (document.visibilityState === 'visible') {
        handlePopupClosedForTilt();
      }
    };

    document.addEventListener('visibilitychange', rearmPeekListener);
    window.addEventListener('focus', rearmPeekListener);
    window.addEventListener('pageshow', rearmPeekListener);

    return () => {
      document.removeEventListener('visibilitychange', rearmPeekListener);
      window.removeEventListener('focus', rearmPeekListener);
      window.removeEventListener('pageshow', rearmPeekListener);
    };
  }, [handlePopupClosedForTilt]);

  // ── On mount: check shuffle setting, run shuffle if needed, then mark ready ──
  useEffect(() => {
    let shuffleEnabled = false;
    try {
      shuffleEnabled = localStorage.getItem(SHUFFLE_STORAGE_KEY) === 'true';
    } catch { /* ignore */ }

    if (shuffleEnabled) {
      setSplashMode(1);
      // Defer to next tick so the splash renders first
      setTimeout(async () => {
        const { buildShuffledCalendar } = await import('@/lib/shuffleCalendar');
        const shuffled = buildShuffledCalendar();
        setSessionCalendar(shuffled);
        setSplashMode(0);
        setAppReady(true);
      }, 50);
    } else {
      setAppReady(true);
    }
  }, []);

  // Once app is ready (and not shuffling), dismiss the splash after a short delay
  useEffect(() => {
    if (appReady && splashMode !== 1) {
      // Give the main content a moment to paint, then close splash
      const timer = setTimeout(() => {
        setSplashVisible(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [appReady, splashMode]);

  // Load override card from cookie on mount
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const saved = loadOverrideCardFromCookie();
    overrideCardRef.current = saved;
    setOverrideCard(saved);
  }, []);

  // Load speechDelayMs from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('tarot-speech-delay');
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
          setSpeechDelayMs(Math.round(parsed * 60 * 1000));
        }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      const savedName = sessionStorage.getItem(STORED_CARD_NAME_KEY);
      if (savedName) setStoredCardName(savedName);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORED_CARD_NAME_KEY) {
        setStoredCardName(event.newValue ?? null);
      }
    };

    const handleCardNameUpdated = (event: CustomEvent<string>) => {
      setStoredCardName(event.detail || null);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('tarot-card-name-updated', handleCardNameUpdated as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('tarot-card-name-updated', handleCardNameUpdated as EventListener);
    };
  }, []);

  // ── Cleanup peek listener on unmount ──
  useEffect(() => {
    return () => {
      if (peekOrientationHandler.current) {
        if (typeof (peekOrientationHandler.current as unknown) === 'function') {
          (peekOrientationHandler.current as unknown as () => void)();
        }
        peekOrientationHandler.current = null;
      }
      peekListenerAttached.current = false;
      if (revealTimeoutRef.current !== null) {
        clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
      // Release wake lock on unmount
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  const weekDays = getWeekDays(weekStart);

  // Auto-scroll to grid on mount
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Prevent pull-to-refresh / overscroll in Chrome PWA by blocking touchmove
  // on the document when the touch started on a non-scrollable area.
  useEffect(() => {
    const preventPullToRefresh = (e: TouchEvent) => {
      // Allow vertical scroll only inside elements that explicitly need it
      // (e.g. modal scroll areas). Block everything else.
      const target = e.target as HTMLElement | null;
      const isScrollable = target?.closest('.overflow-y-auto, .overflow-y-scroll, [data-scrollable]');
      if (!isScrollable) {
        e.preventDefault();
      }
    };
    // Must be non-passive to allow preventDefault
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    return () => document.removeEventListener('touchmove', preventPullToRefresh);
  }, []);

  // Auto-hide header on scroll
  useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;
    const handleScroll = () => {
      const currentY = container.scrollTop;
      const delta = currentY - lastScrollY.current;
      if (delta > 4) setHeaderVisible(false);
      else if (delta < -4) setHeaderVisible(true);
      lastScrollY.current = currentY;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleWindowScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (delta > 4) setHeaderVisible(false);
      else if (delta < -4) setHeaderVisible(true);
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const navigateWeek = useCallback((dir: 'forward' | 'backward') => {
    if (animating) return;
    setSelectedGoToDate(null);
    setDirection(dir);
    setAnimating(true);
    const next = new Date(weekStart);
    next.setDate(next.getDate() + (dir === 'forward' ? 7 : -7));
    setPendingWeekStart(next);
  }, [animating, weekStart]);

  const navigateToDate = useCallback((date: Date, preserveSelectedDate = false) => {
    const targetWeek = getWeekStart(date);
    if (targetWeek.getTime() !== weekStart.getTime()) {
      if (!preserveSelectedDate) {
        setSelectedGoToDate(null);
      }
      const dir = targetWeek > weekStart ? 'forward' : 'backward';
      setDirection(dir);
      setAnimating(true);
      setPendingWeekStart(targetWeek);
    }
  }, [weekStart]);

  const navigateToMonth = useCallback((year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    navigateToDate(firstDay);
  }, [navigateToDate]);

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTitleLongPress = useCallback(() => {
    if (!storedCardName) return;

    clearLongPress();
    longPressTimer.current = window.setTimeout(() => {
      setHeaderSubtitle(formatPeekCardName(storedCardName));
      window.setTimeout(() => setHeaderSubtitle('The Oracle of Days'), 5000);
      longPressTimer.current = null;
    }, 400);
  }, [clearLongPress, storedCardName]);

  const handleTitleRelease = useCallback(() => {
    clearLongPress();
  }, [clearLongPress]);

  useEffect(() => {
    if (!animating || !pendingWeekStart) return;
    const timer = setTimeout(() => {
      setWeekStart(pendingWeekStart);
      setPendingWeekStart(null);
      setAnimating(false);
    }, 280);
    return () => clearTimeout(timer);
  }, [animating, pendingWeekStart]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (popupCard) {
        if (e.key === 'Escape') setPopupCard(null);
        return;
      }
      if (e.key === 'ArrowRight') navigateWeek('forward');
      if (e.key === 'ArrowLeft') navigateWeek('backward');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigateWeek, popupCard]);

  // Touch/swipe
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      navigateWeek(dx < 0 ? 'forward' : 'backward');
    }
    touchStartX.current = null;
  };

  const handleCardClick = (card: TarotCard, date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const displayCard = overrides[dateStr] ?? card;
    setPopupCard({ card: displayCard, date });
  };

  const handleVoiceOverride = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const newCard = overrideCardRef.current;

    setOverrides((prev) => {
      const next = { ...prev };

      // ── Step 1: same-week swap ──────────────────────────────────────────
      // Check if the replacement card already appears on another day in the same week
      const conflictDateStr = weekDays
        .map(d => d.toISOString().split('T')[0])
        .find(ds => {
          if (ds === dateStr) return false;
          const effectiveCard = prev[ds] ?? getCardForDate(new Date(ds));
          return effectiveCard?.id === newCard.id;
        });

      if (conflictDateStr !== undefined) {
        // Swap: the target date gets the conflict day's card, conflict day gets the new card
        const targetCurrentCard = prev[dateStr] ?? getCardForDate(date);
        next[conflictDateStr] = targetCurrentCard!;
        next[dateStr] = newCard;
        return next;
      }

      // ── Step 2: global ±5-week duplicate check ─────────────────────────
      // Scan 35 days before and after the target date for any existing occurrence
      // of the force card (in overrides or base calendar), and swap if found.
      for (let offset = -35; offset <= 35; offset++) {
        if (offset === 0) continue; // skip the target date itself
        const scanDate = new Date(date);
        scanDate.setDate(scanDate.getDate() + offset);
        const scanStr = scanDate.toISOString().split('T')[0];
        const effectiveCard = prev[scanStr] ?? getCardForDate(scanDate);
        if (effectiveCard?.id === newCard.id) {
          // Swap: move the target date's current card to the duplicate slot
          const targetCurrentCard = prev[dateStr] ?? getCardForDate(date);
          next[scanStr] = targetCurrentCard!;
          next[dateStr] = newCard;
          return next;
        }
      }

      // ── No conflict found — plain assignment ───────────────────────────
      next[dateStr] = newCard;
      return next;
    });

    setVoiceResults((prev) => ({ ...prev, [dateStr]: 'blue' }));
  };

  const handleVoiceReset = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[dateStr];
      return next;
    });
  };

  const handleVoiceNotRecognized = () => {
    setVoiceResults((prev) => {
      const next = { ...prev };
      weekDays.forEach((d) => {
        const key = d.toISOString().split('T')[0];
        if (!prev[key] || prev[key] !== 'blue') {
          next[key] = 'red';
        }
      });
      return next;
    });
    setTimeout(() => {
      setVoiceResults((prev) => {
        const next = { ...prev };
        weekDays.forEach((d) => {
          const key = d.toISOString().split('T')[0];
          if (next[key] === 'red') delete next[key];
        });
        return next;
      });
    }, 3000);
  };

  // Handle card picked from the config calculator (force-card mode)
  const handleConfigCardPicked = useCallback((card: TarotCard) => {
    overrideCardRef.current = card;
    setOverrideCard(card);
    saveOverrideCardToCookie(card.id);
    setConfigOpen(false);
  }, []);

  // Refresh speechDelayMs when config modal closes
  const handleConfigClose = useCallback(() => {
    setConfigOpen(false);
    try {
      const stored = localStorage.getItem('tarot-speech-delay');
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
          setSpeechDelayMs(Math.round(parsed * 60 * 1000));
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Handle Go to date confirm
  const handleGoToDateConfirm = useCallback((date: Date) => {
    setSelectedGoToDate(date);
    setGoToDateOpen(false);
    navigateToDate(date, true);
  }, [navigateToDate]);

  // Reading mode: card picked — just close, no override
  const handleReadingCardPicked = useCallback((_card: TarotCard) => {
    setReadingCalculatorOpen(false);
    // Skip the splash title page when returning from hand scan ritual
    setSplashMode(2);
    // Always shuffle when hand scan completes (regardless of "Shuffle on start" setting)
    import('@/lib/shuffleCalendar').then(({ buildShuffledCalendar }) => {
      const shuffled = buildShuffledCalendar();
      setSessionCalendar(shuffled);
      // Bump version so dayCells re-computes with the new shuffled calendar
      setShuffleVersion(v => v + 1);

      // ── Place force card on today's date ───────────────────────────────
      // After the shuffle, assign the force card to today. If the force card
      // already appears on another day in the same week as today, swap it with
      // today's (post-shuffle) card. Otherwise do a global ±5-week duplicate
      // check and swap any found duplicate.
      const forceCard = overrideCardRef.current;
      const todayStr = today.toISOString().split('T')[0];
      const todayWeekStart = getWeekStart(today);
      const todayWeekDays = getWeekDays(todayWeekStart);

      setOverrides((prev) => {
        const next = { ...prev };

        // Step 1: same-week swap
        const sameWeekConflict = todayWeekDays
          .map(d => d.toISOString().split('T')[0])
          .find(ds => {
            if (ds === todayStr) return false;
            const effectiveCard = prev[ds] ?? getCardForDate(new Date(ds));
            return effectiveCard?.id === forceCard.id;
          });

        if (sameWeekConflict !== undefined) {
          const todayCurrentCard = prev[todayStr] ?? getCardForDate(today);
          next[sameWeekConflict] = todayCurrentCard!;
          next[todayStr] = forceCard;
          return next;
        }

        // Step 2: global ±5-week duplicate check
        for (let offset = -35; offset <= 35; offset++) {
          if (offset === 0) continue;
          const scanDate = new Date(today);
          scanDate.setDate(scanDate.getDate() + offset);
          const scanStr = scanDate.toISOString().split('T')[0];
          const effectiveCard = prev[scanStr] ?? getCardForDate(scanDate);
          if (effectiveCard?.id === forceCard.id) {
            const todayCurrentCard = prev[todayStr] ?? getCardForDate(today);
            next[scanStr] = todayCurrentCard!;
            next[todayStr] = forceCard;
            return next;
          }
        }

        // No conflict — plain assignment
        next[todayStr] = forceCard;
        return next;
      });
    }).catch(() => {/* ignore */});
  }, [today]);

  const dayCells: DayCellData[] = weekDays.map((date) => {
    const dateStr = date.toISOString().split('T')[0];
    const staticCard = getCardForDate(date);
    const overriddenCard = overrides[dateStr];
    const isSelectedDate = selectedGoToDate !== null && date.getTime() === selectedGoToDate.getTime();

    return {
      date,
      card: overriddenCard ?? staticCard,
      isToday: date.getTime() === today.getTime(),
      isCurrentMonth: date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear(),
      isOverridden: !!overriddenCard,
      isSelectedDate,
      originalCard: staticCard,
    };
  });

  const animClass = animating ?
    direction === 'forward' ? 'week-exit' : 'week-exit-reverse' :
    direction === 'forward' ? 'week-enter' : 'week-enter-reverse';

  const weekLabel = (() => {
    const first = weekDays[0];
    const last = weekDays[6];
    const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
    if (sameMonth) {
      // e.g. "10 – 16 August 2026"
      const lastStr = last.toLocaleDateString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' });
      return `${first.getDate()} – ${lastStr}`;
    }
    // Cross-month: e.g. "27 July – 2 August 2026"
    const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    const firstStr = first.toLocaleDateString('en-GB', opts);
    const lastStr = last.toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
    return `${firstStr} – ${lastStr}`;
  })();

  return (
    <>
      {/* Splash screen — always shown on load */}
      {splashVisible && (
        <SplashScreen
          splashMode={splashMode}
          onDone={() => setSplashVisible(false)}
          maxDurationMs={10000}
        />
      )}

      <div
        ref={scrollContainer}
        className="flex flex-col bg-background parchment-texture relative overflow-hidden"
        style={{ height: '100dvh', maxHeight: '100dvh' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}>

      <AmbientParticles />

      {/* Top-right menu button */}
      <div ref={menuRef} className="absolute top-3 right-4 z-20">
        <button
          onClick={() => setMenuOpen(prev => !prev)}
          className="w-8 h-8 rounded-full flex items-center justify-center
            font-body text-base text-muted-foreground hover:text-primary transition-colors duration-200
            border border-border/40 hover:border-primary/40 bg-card/60 backdrop-blur-sm"
          title="Menu"
          aria-label="Open menu"
          aria-haspopup="true"
          aria-expanded={menuOpen}>
          …
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute top-10 right-0 w-44 bg-card border border-border/60 rounded-xl shadow-2xl overflow-hidden animate-modal-in z-30">
            {/* Go to date */}
            <button
              onClick={() => { setMenuOpen(false); setGoToDateOpen(true); }}
              className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 border-b border-border/20">
              <span className="text-primary/60 text-sm">📅</span>
              Go to date…
            </button>

            {/* Reading */}
            <button
              onClick={() => { setMenuOpen(false); setReadingCalculatorOpen(true); }}
              className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 border-b border-border/20">
              <span className="text-primary/60 text-sm">🔮</span>
              Reading
            </button>

            {/* Instructions */}
            <a
              href="#"
              onClick={() => setMenuOpen(false)}
              className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 border-b border-border/20 no-underline">
              <span className="text-primary/60 text-sm">📖</span>
              Instructions
            </a>

            {/* DBG gesture tester — only visible when SHOWTEST is true */}
            {SHOWTEST && (
              <a
                href="/dbg"
                onClick={() => setMenuOpen(false)}
                className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 border-b border-border/20 no-underline">
                <span className="text-primary/60 text-sm">🔧</span>
                -DBG-
              </a>
            )}

            {/* Settings */}
            <button
              onClick={() => { setMenuOpen(false); setConfigOpen(true); }}
              className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 border-b border-border/20">
              <span className="text-primary/60 text-sm">⚙️</span>
              Settings
            </button>

            {/* About */}
            <a
              href="/about"
              onClick={() => setMenuOpen(false)}
              className="w-full text-left px-4 py-2.5 font-display text-xs tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2.5 no-underline">
              <span className="text-primary/60 text-sm">ℹ️</span>
              About
            </a>
          </div>
        )}
      </div>

      {/* Header — auto-hides on scroll down */}
      <header
        className={`
          relative z-10 pt-3 pb-2 px-4 sm:px-6 lg:px-8 flex-shrink-0
          transition-all duration-300 ease-in-out overflow-hidden
          ${headerVisible ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0 pt-0 pb-0'}
        `}
        aria-hidden={!headerVisible}>
        <div className="max-w-screen-2xl mx-auto flex flex-col items-center gap-1">
          <div className="flex items-center gap-3">
            <span className="text-primary text-xl animate-gold-pulse select-none" aria-hidden>✦</span>
            <h1
              className="font-decorative text-xl sm:text-2xl text-primary tracking-widest uppercase"
              onMouseDown={handleTitleLongPress}
              onMouseUp={handleTitleRelease}
              onMouseLeave={handleTitleRelease}
              onTouchStart={handleTitleLongPress}
              onTouchEnd={handleTitleRelease}
              onTouchCancel={handleTitleRelease}
            >
              A Tarot a Day
            </h1>
            <span className="text-primary text-xl animate-gold-pulse select-none" aria-hidden>✦</span>
          </div>
          <div className="arcane-divider w-48 sm:w-64" />
          <p className="font-body text-muted-foreground text-[10px] sm:text-xs tracking-wider italic">
            {revealedCardName ?? headerSubtitle}
          </p>
        </div>
      </header>

      {/* Navigation Controls */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 mb-2 flex-shrink-0">
        <div className="max-w-screen-2xl mx-auto flex items-center gap-2">
          <div className="flex-1">
            <NavigationControls
              weekLabel={weekLabel}
              onPrev={() => navigateWeek('backward')}
              onNext={() => navigateWeek('forward')}
              onToday={() => {
                setSelectedGoToDate(null);
                const todayWeek = getWeekStart(today);
                if (todayWeek.getTime() !== weekStart.getTime()) {
                  const dir = todayWeek > weekStart ? 'forward' : 'backward';
                  setDirection(dir);
                  setAnimating(true);
                  setPendingWeekStart(todayWeek);
                }
              }}
              isAtCurrentWeek={getWeekStart(today).getTime() === weekStart.getTime()}
              hideNavButtons={monthSelectorOpen} />
          </div>
        </div>
      </div>

      {/* Calendar Grid — fills remaining height */}
      <main
        ref={gridRef}
        className={`relative z-10 px-4 sm:px-6 lg:px-8 flex-1 min-h-0 pb-3 ${animClass}`}
        aria-label="Weekly tarot calendar">

        <div className="max-w-screen-2xl mx-auto h-full overflow-hidden">
          <div className="grid grid-cols-2 gap-2 sm:gap-3 h-full" style={{ gridTemplateRows: 'repeat(4, minmax(0, 1fr))' }}>
            <DayCell key={`day-${dayCells[0].date.toISOString()}`} data={dayCells[0]} showMonthName={true} starColor={voiceResults[dayCells[0].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[1].date.toISOString()}`} data={dayCells[1]} showMonthName={false} starColor={voiceResults[dayCells[1].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[2].date.toISOString()}`} data={dayCells[2]} showMonthName={false} starColor={voiceResults[dayCells[2].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[3].date.toISOString()}`} data={dayCells[3]} showMonthName={false} starColor={voiceResults[dayCells[3].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[4].date.toISOString()}`} data={dayCells[4]} showMonthName={false} starColor={voiceResults[dayCells[4].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[5].date.toISOString()}`} data={dayCells[5]} showMonthName={false} starColor={voiceResults[dayCells[5].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />
            <DayCell key={`day-${dayCells[6].date.toISOString()}`} data={dayCells[6]} showMonthName={false} starColor={voiceResults[dayCells[6].date.toISOString().split('T')[0]]} onCardClick={handleCardClick} />

            <MiniMonthCalendar
              referenceDate={weekDays[0]}
              today={today}
              weekDays={weekDays}
              isListening={isListening}
              onListeningChange={setIsListening}
              onVoiceOverride={handleVoiceOverride}
              onVoiceReset={handleVoiceReset}
              onVoiceNotRecognized={handleVoiceNotRecognized}
              overrides={overrides}
              speechDelayMs={speechDelayMs}
              onNavigateToMonth={navigateToMonth}
              onMonthSelectorToggle={setMonthSelectorOpen} />
          </div>
        </div>
      </main>

      {/* Card reading popup */}
      {popupCard &&
        <TarotCardPopup
          card={popupCard.card}
          date={popupCard.date}
          onClose={() => setPopupCard(null)}
          onPopupClosedForTilt={handlePopupClosedForTilt}
          overrides={overrides}
          fromCalendar={true} />
      }

      {/* Config Modal */}
      {configOpen && <ConfigModal onClose={handleConfigClose} onCardPicked={handleConfigCardPicked} currentCard={overrideCard} />}

      {/* Reading mode calculator (no force-card, no override) */}
      {readingCalculatorOpen && (
        <CardPickerCalculator
          onConfirm={handleReadingCardPicked}
          onClose={() => setReadingCalculatorOpen(false)}
          readingMode={true}
        />
      )}

      {/* Go to date popup */}
      {goToDateOpen && (
        <GoToDatePopup
          onConfirm={handleGoToDateConfirm}
          onClose={() => setGoToDateOpen(false)}
        />
      )}
    </div>
  </>
);
}
